import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import currentTimeZone from '../../../../config/timeZone';
import { useAppSelector } from '../../../../redux/hooks';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagementApi from '../../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  total: number;
  monthNumber?: number | null;
  monthName?: string | null;
  year?: number | null;
}
/**
 * Transaction charts component
 * @returns {any} The rendered component
 */
function TransactionCharts() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('sidePanel');
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [revenueGrowthData, setRevenueGrowthData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  const chartTimePeriod: {
    label: string;
    value: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';
  }[] = useMemo(
    () => [
      {
        label: t('last24hours'),
        value: 'last24hours',
      },
      {
        label: t('last7days'),
        value: 'last7days',
      },
      {
        label: t('lastMonth'),
        value: 'lastMonth',
      },
      {
        label: t('last12months'),
        value: 'last12months',
      },
    ],
    [t, i18n.language],
  );

  /** Build chart datasets */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const buildChartDataset = (label: string, data: number[]) => ({
    label,
    data,
    borderColor: '#F79009',
    /**
     * backgroundColor
     * @param {any} context The dataset context
     * @returns {any} The gradient background
     */
    backgroundColor: (context: any) => {
      const { chart } = context;
      const { ctx } = chart;
      const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);
      gradient.addColorStop(0, 'rgba(247,144,9,0.4)');
      gradient.addColorStop(1, 'rgba(237,237,237,0)');
      return gradient;
    },
    fill: 'origin',
    tension: 0.4,
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 4,
  });

  const powerConsumedChartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      buildChartDataset(
        t('powerConsumed'),
        powerConsumedData.map((d) => d.total / 1000),
      ),
    ],
  };

  const revenueGrowthChartData = {
    labels: revenueGrowthData.map((d) => d.label),
    datasets: [
      buildChartDataset(
        t('revenue'),
        revenueGrowthData.map((d) => d.total),
      ),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @param {any} context The tooltip context
         * @returns {string} The background color
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === t('revenue')) {
              return 'rgba(247, 144, 9, 0.9)'; // Orange for Revenue
            }
            if (context.tooltipItems[0].dataset.label === t('powerConsumed')) {
              return 'rgba(247, 144, 9, 0.9)'; // Green for Electricity
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param {any} context The tooltip context
           * @returns {string} The formatted title
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === t('powerConsumed') ? t('powerConsumed') : t('revenueGrowth');
          },
          /**
           * Function to show tooltip data
           * @param {any} context The tooltip context
           * @returns {string} The formatted label
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === t('revenue') ? branding.text.currency : t('kwh')}`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on time range
   * @param {'last24hours' | 'last7days' | 'lastMonth' | 'last12months'} range The time range
   */
  const fetchTransactionCharts = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    setIsLoading(true);
    try {
      // Fetch both in parallel
      const [powerResponse, revenueResponse] = await Promise.all([
        stationManagementApi.fetchTransactionChart({
          timePeriod: range,
          type: 'powerConsumption',
          timeZone: currentTimeZone,
          chargingStation: selectedChargingPoint?.id,
        }),
        stationManagementApi.fetchTransactionChart({
          timePeriod: range,
          type: 'revenue',
          timeZone: currentTimeZone,
          chargingStation: selectedChargingPoint?.id,
        }),
      ]);

      // If user switched time range, ignore outdated results
      if (activeRangeReference.current !== range) return;

      /**
       * Helper to process results based on range
       * @param {any[]} data The raw data
       * @param {number} status The response status
       * @returns {any[]} The processed data
       */
      const processData = (data: any[], status: number) => {
        if (status !== 200) return [];
        switch (range) {
          case 'last12months':
            return fillMissingMonthsGeneric(data, i18n.language);
          case 'lastMonth':
            return fillMissingLastNDaysGeneric(data, 30, i18n.language);
          case 'last7days':
            return fillMissingLastNDaysGeneric(data, 7, i18n.language);
          case 'last24hours':
            return fillMissingLast24HrsGeneric(data);
          default:
            return data;
        }
      };
      setPowerConsumedData(processData(powerResponse.data, powerResponse.status));
      setRevenueGrowthData(processData(revenueResponse.data, revenueResponse.status));
    } catch (error) {
      console.error('Error fetching transaction chart data:', error);
    } finally {
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };
  useEffect(() => {
    fetchTransactionCharts('last24hours');
  }, []);

  return (
    <div className="transaction-charts-wrap" dir={i18n.dir()}>
      <div className="transaction-charts-container">
        <div className="transaction-charts-header">
          <CustomToggleButton
            labels={chartTimePeriod.map((d) => d.label)}
            onToggle={(index) => {
              const range = chartTimePeriod[index]?.value || 'last24hours';
              fetchTransactionCharts(range);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        <div className="transaction-chart-content">
          {isLoading ? (
            <center className="transaction-power-consumed-chart-loader">
              <BatteryLoader />
            </center>
          ) : (
            <>
              <p className="chart-label">{t('powerConsumed')}</p>
              <div className="transaction-power-consumed-chart-wrap">
                <Line data={powerConsumedChartData} options={options} />
              </div>
            </>
          )}
        </div>
        <div className="transaction-chart-content">
          {isLoading ? (
            <center className="transaction-revenue-growth-chart-loader">
              <BatteryLoader />
            </center>
          ) : (
            <>
              <p className="chart-label">{t('revenueGrowth')}</p>
              <div className="transaction-revenue-growth-chart-wrap">
                <Line data={revenueGrowthChartData} options={options} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TransactionCharts;
